import React, { useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    EditButton,
    Create,
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    FunctionField,
    required,
    email,
    useRecordContext,
    useNotify,
    useRefresh,
} from 'react-admin';
import { Alert, Box, Button, Chip, Divider, Typography } from '@mui/material';
import { FormToolbar } from '../../common/FormToolbar';
import { APIUrl } from '../../common/apiConfig';
import { AssignmentPanel } from './AssignmentPanel';
import { PasswordOnce } from './PasswordOnce';

/**
 * Teachers.
 *
 * School administrators only. A teacher does not get the staff list -- it is
 * not theirs to browse, and the API refuses them with a 403 regardless of what
 * this screen chooses to render.
 *
 * NO DELETE BUTTON
 * ----------------
 * "Remove" ends the membership, deletes the assignments and revokes the live
 * tokens, but keeps the person and their history. Deleting the row instead
 * would take their classes' record with them, which is not what "this teacher
 * left" means -- and an administrator with a delete button on a staff list is
 * one mis-click away from an unrecoverable one.
 */

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('jwt_access_token')}`,
});

// =========================================================================
// List
// =========================================================================

const filters = [
    <TextInput source="q" label="Search" alwaysOn resettable />,
    <SelectInput
        source="status"
        label="Status"
        choices={[
            { id: 'active', name: 'Active' },
            { id: 'inactive', name: 'Left' },
        ]}
    />,
    <SelectInput
        source="unassigned"
        label="Assignment"
        choices={[{ id: 1, name: 'Has no class yet' }]}
    />,
];

const TeacherList = props => (
    <List
        title="Teachers"
        {...props}
        filters={filters}
        filterDefaultValues={{ status: 'active' }}
        sort={{ field: 'full_name', order: 'ASC' }}
        exporter={false}
        empty={
            <Alert severity="info" sx={{ m: 3 }}>
                No teachers yet. Add one, then assign them a class section &mdash; a teacher with no
                class can see nothing at all.
            </Alert>
        }
    >
        <Datagrid rowClick="edit" bulkActionButtons={false}>
            <EditButton />
            <TextField source="full_name" label="Name" />
            <TextField source="email" label="Login" />
            <TextField source="employee_no" label="Employee no." />
            <TextField source="phone" label="Phone" />

            {/*
              The most useful column on the screen. A teacher with 0 sections
              can see nothing, and after a bulk import that is most of them.
            */}
            <FunctionField
                label="Can see"
                sortBy="id"
                render={(r: any) =>
                    r.can_see_anything ? (
                        <Chip
                            size="small"
                            color="success"
                            label={`${r.section_count} section${r.section_count === 1 ? '' : 's'}`}
                        />
                    ) : (
                        <Chip size="small" color="warning" label="nothing yet" />
                    )
                }
            />

            <FunctionField
                label="Status"
                sortBy="status"
                render={(r: any) => (
                    <Chip
                        size="small"
                        label={r.status === 'active' ? 'active' : 'left'}
                        color={r.status === 'active' ? 'default' : 'error'}
                    />
                )}
            />
        </Datagrid>
    </List>
);

// =========================================================================
// Create
// =========================================================================

const TeacherCreate = props => {
    const [created, setCreated] = useState<any>(null);

    const onSuccess = (data: any) => setCreated(data);

    if (created) {
        return (
            <Box sx={{ p: 3 }}>
                <PasswordOnce password={created.password_once} login={created.email} />
                <Typography variant="body2" sx={{ mb: 2 }}>
                    <b>{created.full_name}</b> has been added, but has no class assignment yet, so
                    they can see nothing. Open them to assign a section.
                </Typography>
                <Button variant="contained" href={`#/school-teacher/${created.id}`}>
                    Assign their classes
                </Button>
                <Button sx={{ ml: 2 }} href="#/school-teacher">
                    Back to the list
                </Button>
            </Box>
        );
    }

    return (
        <Create {...props} title="Add a teacher" mutationOptions={{ onSuccess }}>
            <SimpleForm toolbar={<FormToolbar {...props} />}>
                <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                    This creates their login. A password is generated and shown{' '}
                    <b>once, on the next screen</b> &mdash; write it down before leaving the page.
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <TextInput
                        source="username"
                        label="Login"
                        validate={[required(), email()]}
                        helperText="Their email address. It cannot be changed later. Maximum 50 characters."
                        sx={{ flex: 2 }}
                    />
                    <TextInput
                        source="full_name"
                        label="Full name"
                        validate={[required()]}
                        sx={{ flex: 2 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <TextInput source="employee_no" label="Employee number" sx={{ flex: 1 }} />
                    <TextInput source="phone" label="Phone" sx={{ flex: 1 }} />
                </Box>
            </SimpleForm>
        </Create>
    );
};

// =========================================================================
// Edit
// =========================================================================

const TeacherPanels = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();

    const [assignments, setAssignments] = useState<any[] | null>(null);
    const [password, setPassword] = useState<string | null>(null);

    if (!record) return null;

    const list = assignments ?? record.assignments ?? [];

    const post = async (path: string, body: any) => {
        try {
            const res = await fetch(`${APIUrl}school-teacher/${path}/${record.id}`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(body),
            });
            const json = await res.json();
            if (json.status !== 'success') {
                notify(json.message || 'That did not work.', { type: 'error' });
                return null;
            }
            notify(json.message, { type: 'success' });
            return json.result;
        } catch (e: any) {
            notify(e.message || 'That did not work.', { type: 'error' });
            return null;
        }
    };

    const resetPassword = async () => {
        const r = await post('reset_password', {});
        if (r && r.password_once) setPassword(r.password_once);
    };

    const remove = async () => {
        if (
            !window.confirm(
                `Remove ${record.full_name}? They will be signed out immediately and lose access ` +
                    `to every class. Their record and history stay.`
            )
        ) {
            return;
        }
        const r = await post('remove', {});
        if (r) {
            refresh();
            window.location.hash = '#/school-teacher';
        }
    };

    return (
        <>
            {password && (
                <PasswordOnce
                    password={password}
                    login={record.email}
                    onDone={() => setPassword(null)}
                />
            )}

            <AssignmentPanel
                teacherId={Number(record.id)}
                assignments={list}
                onChange={setAssignments}
            />

            <Divider sx={{ my: 2, width: '100%' }} />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="outlined" onClick={resetPassword}>
                    Reset password
                </Button>
                <Button variant="outlined" color="error" onClick={remove}>
                    Remove from school
                </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                Removing ends their access at once &mdash; their current sign-in stops working
                immediately, not at the end of the day. Their record and their classes' history are
                kept.
            </Typography>
        </>
    );
};

/** Shown rather than editable -- see index_put()'s refusal on the API side. */
const ImmutableLogin = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <TextInput
            source="email"
            label="Login"
            disabled
            helperText="A login cannot be changed. Remove and re-add under a new one if it must change."
            sx={{ flex: 2 }}
        />
    );
};

const TeacherEdit = props => (
    <Edit {...props} title="Teacher" mutationMode="pessimistic">
        <SimpleForm toolbar={<FormToolbar {...props} />}>
            <TeacherPanels />

            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <ImmutableLogin />
                <TextInput
                    source="full_name"
                    label="Full name"
                    validate={[required()]}
                    sx={{ flex: 2 }}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <TextInput source="employee_no" label="Employee number" sx={{ flex: 1 }} />
                <TextInput source="phone" label="Phone" sx={{ flex: 1 }} />
            </Box>
        </SimpleForm>
    </Edit>
);

export default { list: TeacherList, create: TeacherCreate, edit: TeacherEdit };
